import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAccess } from "@/hooks/useAccess";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { GraduationCap, Timer, Repeat, Loader2, Lock } from "lucide-react";

const MODES = [
  { k: "practice", title: "Practice Mode", desc: "No timer. Instant feedback and explanations after each answer.", icon: GraduationCap },
  { k: "mock", title: "Timed Mock Exam", desc: "Simulated exam with timer, shuffled options, and results at the end.", icon: Timer },
  { k: "revision", title: "Revision Mode", desc: "Replay incorrect and bookmarked questions until you master them.", icon: Repeat },
];

export default function QuizSetup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [mode, setMode] = useState(params.get("mode") || "practice");
  const [subjectId, setSubjectId] = useState(params.get("subject") || "all");
  const [chapterId, setChapterId] = useState("all");
  const [numQ, setNumQ] = useState(10);
  const [duration, setDuration] = useState(30);
  const [starting, setStarting] = useState(false);

  const { access, can } = useAccess();
  useEffect(() => {
    api.get("/subjects").then((r) => setSubjects(r.data));
  }, []);
  const allowedSubjects = subjects.filter((s) => can(s.id));
  useEffect(() => {
    if (subjectId && subjectId !== "all") {
      api.get(`/topics?subject_id=${subjectId}`).then((r) => setChapters(r.data));
    } else {
      setChapters([]);
    }
    setChapterId("all");
  }, [subjectId]);

  const practiceCount = chapterId !== "all"
    ? chapters.find((c) => c.id === chapterId)?.question_count ?? 0
    : subjectId !== "all"
      ? subjects.find((s) => s.id === subjectId)?.question_count ?? 0
      : allowedSubjects.reduce((n, s) => n + (s.question_count || 0), 0);

  const start = async () => {
    setStarting(true);
    try {
      const payload = {
        mode,
        subject_id: subjectId === "all" ? null : subjectId,
        topic_id: chapterId === "all" ? null : chapterId,
        num_questions: mode === "practice" ? null : Number(numQ),
        duration_minutes: mode === "mock" ? Number(duration) : 0,
      };
      const { data } = await api.post("/quiz/start", payload);
      navigate(`/quiz/${data.session_id}`, { state: { quiz: data } });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setStarting(false); }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      <div className="mb-8">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[hsl(var(--brand-orange))]">Start a quiz</div>
        <h1 className="mt-1 font-display text-3xl font-bold">Set up your session</h1>
        <p className="mt-1 text-sm text-muted-foreground">Pick a mode, a subject and a chapter — then start.</p>
      </div>

      {access && !access.all && access.ids.size === 0 && (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="no-access-banner">
          <span className="flex items-center gap-2"><Lock className="h-4 w-4" /> You are not enrolled in any course yet. Enroll in a course to unlock practice questions.</span>
          <Button asChild size="sm" className="bg-[hsl(var(--brand-orange))]"><Link to="/courses">Browse courses</Link></Button>
        </div>
      )}
      <Card className="border-border/70">
        <CardContent className="p-6">
          <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mode</Label>
          <RadioGroup
            value={mode}
            onValueChange={setMode}
            className="mt-3 grid gap-3 sm:grid-cols-3"
            data-testid="mode-radio-group"
          >
            {MODES.map((m) => (
              <label
                key={m.k}
                htmlFor={`mode-${m.k}`}
                className={`cursor-pointer rounded-xl border p-4 transition ${
                  mode === m.k
                    ? "border-[hsl(var(--brand-orange))] bg-[hsl(var(--brand-orange))]/5 ring-1 ring-[hsl(var(--brand-orange))]"
                    : "border-border hover:bg-secondary"
                }`}
                data-testid={`mode-option-${m.k}`}
              >
                <div className="flex items-center justify-between">
                  <m.icon className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
                  <RadioGroupItem value={m.k} id={`mode-${m.k}`} />
                </div>
                <div className="mt-3 font-display text-sm font-bold">{m.title}</div>
                <div className="mt-1 text-xs text-muted-foreground">{m.desc}</div>
              </label>
            ))}
          </RadioGroup>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <div>
              <Label>Subject</Label>
              <Select value={subjectId} onValueChange={setSubjectId}>
                <SelectTrigger data-testid="subject-select" className="mt-1.5">
                  <SelectValue placeholder="All subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All subjects</SelectItem>
                  {allowedSubjects.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Chapter</Label>
              <Select value={chapterId} onValueChange={setChapterId} disabled={subjectId === "all" || chapters.length === 0}>
                <SelectTrigger data-testid="chapter-select" className="mt-1.5">
                  <SelectValue placeholder="All chapters" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All chapters</SelectItem>
                  {chapters.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} ({c.question_count})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {subjectId === "all" && <div className="mt-1 text-xs text-muted-foreground">Pick a subject to filter by chapter.</div>}
            </div>
            {mode === "practice" ? (
              <div className="rounded-lg border border-dashed border-border px-4 py-3 text-sm sm:col-span-2" data-testid="practice-all-questions-note">
                <span className="font-semibold">{practiceCount} questions</span>
                <span className="text-muted-foreground"> available. You'll practise all of them — use Next to move through each one.</span>
              </div>
            ) : (
            <div>
              <Label htmlFor="num">Number of questions</Label>
              <Input
                id="num"
                type="number" min={1} max={100}
                value={numQ}
                onChange={(e) => setNumQ(e.target.value)}
                data-testid="num-questions-input"
                className="mt-1.5"
              />
            </div>
            )}
            {mode === "mock" && (
              <div>
                <Label htmlFor="dur">Duration (minutes)</Label>
                <Input
                  id="dur"
                  type="number" min={1} max={240}
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  data-testid="duration-input"
                  className="mt-1.5"
                />
              </div>
            )}
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)} data-testid="setup-cancel-btn">Cancel</Button>
            <Button
              onClick={start}
              disabled={starting}
              className="bg-[hsl(var(--brand-orange))] hover:bg-[hsl(var(--brand-orange))]/90"
              data-testid="setup-start-btn"
            >
              {starting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Start quiz
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
